import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  FetchOrdersResponse,
  FilterFieldsType,
  IOrder,
  OrderProductsInfosType,
  OrdersBodyType,
  OrdersFilterType,
  OrdersInfoType,
  OrdersSliceState,
} from "./types";
import $api from "@/shared/http";
import { ApiRejectPayload, createApiRejectPayload } from "@/shared/http/types";
import { revertAll } from "@/app/model/actions";
import { PageSizeType } from "@/app/model/types";
import { formatCreatedFilterField } from "./lib/formatCreatedFilter";

const initialState: OrdersSliceState = {
  ordersList: [],
  selectedOrderData: null,
  needUpdate: false,
  allOrdersId: [],
  filters: [],
  allCreatedDates: [],
  onlyMyDealers: false,
  selectedOrdersId: [],
  totalAmount: 0,
  totalCount: 0,
  selectedOrderFilters: {},
  orderProductsInfos: [],
  ordersInfo: {
    count: 0,
    priceSumm: 0,
  },
  error: null,
  isLoading: false,
  isFilterLoading: false,
  selectedDates: undefined,
  sortRules: {
    type: "desc",
    field: "created",
  },
  searchCode: null,
  pageSize: (+localStorage.getItem("psizeN") as PageSizeType) || 20,
  totalItemsCount: 0,
  selectedOrderIdNum: null,
  canCreate: false,
};

const orderThunkRejectConfig = { rejectValue: {} as ApiRejectPayload };

/* Загрузка заказов */
export const fetchOrders = createAsyncThunk<
  FetchOrdersResponse,
  "" | OrdersBodyType,
  typeof orderThunkRejectConfig
>("orders/fetchOrders", async (value = "", { rejectWithValue }) => {
  let body = {
    order: {
      type: "desc",
      field: "created",
    },
    pagination: {
      pageSize: +localStorage.getItem("psizeN") || 20,
      pageNumber: 1,
    },
    filters: [] as object[],
  };

  if (value) {
    const { filters, order, pagination } = value;

    body = {
      order,
      pagination,
      filters,
    };
  }

  try {
    const response = await $api.post<FetchOrdersResponse>(
      "/order",
      JSON.stringify(body),
    );
    return response.data;
  } catch (error) {
    return rejectWithValue(createApiRejectPayload(error));
  }
});

/* Загрузка фильтров заказов */
export const fetchFilters = createAsyncThunk<
  OrdersFilterType,
  FilterFieldsType | [],
  typeof orderThunkRejectConfig
>("orders/fetchFilters", async (fields = [], { getState, rejectWithValue }) => {
  const { allCreatedDates } = (getState() as { orders: OrdersSliceState })
    .orders;
  const fieldsForRequest = formatCreatedFilterField(fields, allCreatedDates);

  const body = {
    filters: Object.keys(fieldsForRequest).length
      ? [{ type: "eq", fields: fieldsForRequest }]
      : [],
  };

  try {
    const response = await $api.post("/filters/order", body);
    return response.data?.filters;
  } catch (error) {
    return rejectWithValue(createApiRejectPayload(error));
  }
});

/* Загрузка заказа по ид */
export const getOneOrderById = createAsyncThunk<
  IOrder,
  string,
  typeof orderThunkRejectConfig
>("orders/getOneOrderById", async (id, { rejectWithValue }) => {
  try {
    const response = await $api.get<IOrder>(`/order/${id}`);
    return response.data;
  } catch (error) {
    return rejectWithValue(createApiRejectPayload(error));
  }
});

/* Загрузка продуктов заказа */
export const fetchOrderProductsInfo = createAsyncThunk<
  OrderProductsInfosType[],
  void,
  typeof orderThunkRejectConfig
>("orders/fetchOrderProductsInfo", async (_, { rejectWithValue }) => {
  try {
    const response = await $api.post<OrderProductsInfosType[]>(
      "/order/products/info",
    );
    return response.data;
  } catch (error) {
    return rejectWithValue(createApiRejectPayload(error));
  }
});

/* Загрузка информации заказа */
export const fetchOrdersInfo = createAsyncThunk<
  OrdersInfoType,
  "" | Pick<OrdersBodyType, "filters">,
  typeof orderThunkRejectConfig
>("orders/fetchOrdersInfo", async (value = "", { rejectWithValue }) => {
  let body = {
    filters: [] as object[],
  };

  if (value) {
    const { filters } = value;

    body = {
      filters,
    };
  }

  try {
    const response = await $api.post<OrdersInfoType>(
      "/order/info",
      JSON.stringify(body),
    );
    return response.data;
  } catch (error) {
    return rejectWithValue(createApiRejectPayload(error));
  }
});

export const fetchCreateStatus = createAsyncThunk<
  boolean,
  void,
  typeof orderThunkRejectConfig
>("orders/fetchCreateStatus", async (_, { rejectWithValue }) => {
  try {
    const response = await $api.post<boolean>("/order/remain/can-create");
    return response.data;
  } catch (error) {
    return rejectWithValue(createApiRejectPayload(error));
  }
});

export const ordersSlice = createSlice({
  name: "orders",
  initialState,
  reducers: {
    setOrdersListMock(state, action) {
      state.ordersList = action.payload;
    },
    setOnlyMyDealers(state, action) {
      state.onlyMyDealers = action.payload;
    },
    setSelectedOrderData(state, action) {
      state.selectedOrderData = action.payload;
    },
    setSelectedOrderFilters(state, action) {
      state.searchCode = null;
      state.selectedOrderFilters = action.payload;
    },
    setSelectedDates(state, action) {
      state.searchCode = null;
      state.selectedDates = action.payload;
      if (action.payload.length) {
        state.selectedOrderFilters.created = action.payload;
      } else {
        const { created, ...rest } = state.selectedOrderFilters;
        state.selectedOrderFilters = rest;
      }
    },
    setSortRules(state, action: PayloadAction<typeof state.sortRules>) {
      state.sortRules = action.payload;
    },
    setSearchCode(state, action: PayloadAction<typeof state.searchCode>) {
      state.selectedDates = [];
      state.selectedOrderFilters = {};
      state.searchCode = action.payload;
    },
    setPageSize(state, action: PayloadAction<typeof state.pageSize>) {
      state.pageSize = action.payload;
    },
    setSelectedOrderNum(
      state,
      action: PayloadAction<typeof state.selectedOrderIdNum>,
    ) {
      state.selectedOrderIdNum = action.payload;
    },
    setNeedUpdate(state, action: PayloadAction<typeof state.needUpdate>) {
      state.needUpdate = action.payload;
    },
    clearOrdersError(state) {
      state.error = null;
      state.isLoading = false;
    },
    resetFilters(state) {
      state.searchCode = null;
      state.selectedDates = undefined;
      state.selectedOrderFilters = {};
      state.onlyMyDealers = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(revertAll, () => initialState);

    builder.addCase(fetchOrders.pending, (state) => {
      state.error = null;
      state.isLoading = true;
    });

    builder.addCase(
      fetchOrders.fulfilled,
      (state, action: PayloadAction<FetchOrdersResponse>) => {
        const res = action.payload;
        state.ordersList = res.items;
        const ids = res.items?.reduce((acc, order) => {
          return (acc = [...acc, order.id]);
        }, []);
        state.allOrdersId = ids;
        state.needUpdate = false;
        state.isLoading = false;
        state.totalItemsCount = res.totalCount;
      },
    );

    builder.addCase(fetchOrders.rejected, (state, action) => {
      state.error = action.payload;
      state.isLoading = false;
    });

    /* Загрузка фильтров заказов */
    builder.addCase(fetchFilters.pending, (state) => {
      state.isFilterLoading = true;
      state.error = null;
    });

    builder.addCase(fetchFilters.fulfilled, (state, action) => {
      state.filters = action.payload;
      state.isFilterLoading = false;

      /* Полный справочник дат берём только из нефильтрованного запроса */
      const requestedFields = action.meta.arg;
      if (!requestedFields || !Object.keys(requestedFields).length) {
        state.allCreatedDates = action.payload?.[0]?.fields?.created || [];
      }
    });

    builder.addCase(fetchFilters.rejected, (state, action) => {
      state.isFilterLoading = false;
      state.error = action.payload;
    });

    /* Загрузка продуктов заказа */
    builder.addCase(fetchOrderProductsInfo.pending, (state) => {
      state.error = null;
      state.isLoading = true;
    });

    builder.addCase(
      fetchOrderProductsInfo.fulfilled,
      (state, action: PayloadAction<OrderProductsInfosType[]>) => {
        state.orderProductsInfos = action.payload;
        state.isLoading = false;
      },
    );

    builder.addCase(fetchOrderProductsInfo.rejected, (state, action) => {
      state.error = action.payload;
      state.isLoading = false;
    });

    /* Загрузка информации заказа */
    builder.addCase(fetchOrdersInfo.pending, (state) => {
      state.error = null;
      state.isLoading = true;
    });
    builder.addCase(
      fetchOrdersInfo.fulfilled,
      (state, action: PayloadAction<OrdersInfoType>) => {
        state.ordersInfo = action.payload;
        state.isLoading = false;
      },
    );
    builder.addCase(fetchOrdersInfo.rejected, (state, action) => {
      state.error = action.payload;
      state.isLoading = false;
    });
    /* Загрузка заказа по ид */
    builder.addCase(getOneOrderById.pending, (state) => {
      state.error = null;
      state.isLoading = true;
    });
    builder.addCase(
      getOneOrderById.fulfilled,
      (state, action: PayloadAction<IOrder>) => {
        state.selectedOrderData = action.payload;
        state.isLoading = false;
      },
    );
    builder.addCase(getOneOrderById.rejected, (state, action) => {
      state.error = action.payload;
      state.isLoading = false;
    });
    builder.addCase(fetchCreateStatus.pending, (state) => {
      state.error = null;
    });
    builder.addCase(fetchCreateStatus.fulfilled, (state, action) => {
      state.canCreate = action.payload;
    });
    builder.addCase(fetchCreateStatus.rejected, (state, action) => {
      state.error = action.payload;
    });
  },
});

export const {
  setOrdersListMock,
  setSelectedOrderFilters,
  setSelectedDates,
  setSelectedOrderData,
  setSortRules,
  setSearchCode,
  setPageSize,
  setOnlyMyDealers,
  setSelectedOrderNum,
  clearOrdersError,
  resetFilters,
  setNeedUpdate,
} = ordersSlice.actions;
